import axios from 'axios'
import { useSuspenseQuery } from '@tanstack/react-query'
import { z } from 'zod'

const getContentPages = async ({
  competition_id,
}: {
  competition_id?: number
}) => {
  const response = await axios.get('/api/content_pages', {
    params: competition_id ? { competition_id } : undefined,
  })
  return ContentPagesResponseSchema.parse(response.data)
}

export const useContentPages = ({
  competition_id,
}: {
  competition_id?: number
} = {}) => {
  return useSuspenseQuery({
    queryKey: ['contentPages', { competition_id }],
    queryFn: () => getContentPages({ competition_id }),
  })
}

const getContentPageDetails = async ({ slug }: { slug: string }) => {
  const response = await axios.get(`/api/content_pages/${slug}`)
  return ContentPageDetailSchema.parse(response.data)
}

export const useContentPageDetails = ({ slug }: { slug: string }) => {
  return useSuspenseQuery({
    queryKey: ['contentPageDetails', { slug }],
    queryFn: () => getContentPageDetails({ slug }),
  })
}

export const ContentPageSchema = z.object({
  id: z.number(),
  title: z.string(),
  slug: z.string(),
  order: z.number(),
})

export const ContentPageSectionSchema = z.object({
  title: z.string(),
  content: z.string(),
  image: z.record(z.string()).nullable().optional(),
  document: z.record(z.string()).nullable().optional(),
  external_link: z.string().nullable().optional(),
  order: z.number(),
})

export const ContentPageDetailSchema = z.object({
  id: z.number(),
  title: z.string(),
  slug: z.string(),
  content: z.string(),
  sections: z.array(ContentPageSectionSchema),
})

export const ContentPagesResponseSchema = z.array(ContentPageSchema)

export type ContentPage = z.infer<typeof ContentPageSchema>
export type ContentPageSection = z.infer<typeof ContentPageSectionSchema>
export type ContentPageDetail = z.infer<typeof ContentPageDetailSchema>
export type ContentPagesResponse = z.infer<typeof ContentPagesResponseSchema>
